import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, CheckCircle, Heart, Sparkles, ArrowRight } from "lucide-react";

const ApplicationConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  dealData,
  isLoading,
}) => {
  const handleConfirm = () => {
    onConfirm();
  };

  const handleClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            className="fixed inset-0 z-50 bg-black bg-opacity-60 backdrop-blur-md"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8, y: 50 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 50 }}
            transition={{
              type: "spring",
              damping: 20,
              stiffness: 300,
              duration: 0.6,
            }}
            className="flex overflow-y-auto fixed inset-0 z-50 justify-center items-center p-4"
          >
            <div className="my-8 w-full max-w-lg">
              <div className="overflow-hidden relative bg-white rounded-3xl border border-gray-100 shadow-2xl">
                {/* Decorative Background Elements */}
                <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-[#F2640F] via-[#FF6B6B] to-[#FF0000]"></div>

                {/* Floating Sparkles */}
                <div className="absolute top-4 right-4">
                  <motion.div
                    animate={{
                      rotate: 360,
                      scale: [1, 1.2, 1],
                    }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      ease: "linear",
                    }}
                  >
                    <Sparkles className="w-6 h-6 text-yellow-400" />
                  </motion.div>
                </div>

                {/* Header */}
                <div className="relative p-6 pb-4">
                  <button
                    onClick={handleClose}
                    disabled={isLoading}
                    className="absolute top-3 right-3 p-2 text-gray-400 transition-colors hover:text-gray-600 disabled:opacity-50"
                  >
                    <X className="w-5 h-5" />
                  </button>

                  {/* Success Icon */}
                  <div className="flex justify-center mb-4">
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{
                        type: "spring",
                        damping: 15,
                        stiffness: 300,
                        delay: 0.2,
                      }}
                      className="relative"
                    >
                      <div className="flex justify-center items-center w-16 h-16 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full shadow-lg">
                        <CheckCircle className="w-8 h-8 text-white" />
                      </div>
                      {/* Pulse effect */}
                      <motion.div
                        animate={{
                          scale: [1, 1.2, 1],
                          opacity: [0.7, 0, 0.7],
                        }}
                        transition={{
                          duration: 2,
                          repeat: Infinity,
                          ease: "easeInOut",
                        }}
                        className="absolute inset-0 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full"
                      />
                    </motion.div>
                  </div>

                  {/* Title */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="mb-3 text-center"
                  >
                    <h2 className="mb-2 text-xl font-bold text-gray-900">
                      Ready to Start Earning? 🎉
                    </h2>
                    <p className="text-sm leading-relaxed text-gray-600">
                      You're about to apply to promote this amazing deal and
                      start earning commissions!
                    </p>
                  </motion.div>
                </div>

                {/* Content */}
                <div className="px-6 pb-4">
                  {/* Simple Message */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="mb-4 text-center"
                  >
                    <p className="text-sm text-gray-600">
                      You'll get a shareable link to promote this deal and earn
                      commissions! 💰
                    </p>
                  </motion.div>

                  {/* Action Buttons */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 }}
                    className="space-y-2"
                  >
                    <button
                      onClick={handleConfirm}
                      disabled={isLoading}
                      className="w-full flex justify-center items-center gap-2 px-4 py-3 bg-gradient-to-r from-[#F2640F] to-[#FF0000] text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-orange-200 transition-all duration-300 disabled:opacity-70 disabled:cursor-not-allowed group"
                    >
                      {isLoading ? (
                        <>
                          <motion.div
                            animate={{ rotate: 360 }}
                            transition={{
                              duration: 1,
                              repeat: Infinity,
                              ease: "linear",
                            }}
                            className="w-4 h-4 rounded-full border-2 border-white border-t-transparent"
                          />
                          <span className="text-sm">Applying...</span>
                        </>
                      ) : (
                        <>
                          <Heart className="w-4 h-4 transition-transform group-hover:scale-110" />
                          <span className="text-sm">Yes, Apply Now!</span>
                          <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                        </>
                      )}
                    </button>

                    <button
                      onClick={handleClose}
                      disabled={isLoading}
                      className="px-4 py-2 w-full text-sm font-medium text-gray-600 rounded-xl transition-colors hover:bg-gray-50 disabled:opacity-50"
                    >
                      Maybe Later
                    </button>
                  </motion.div>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ApplicationConfirmationModal;
